/* eslint-disable no-param-reassign */
import { produce } from 'immer';

import { ALGO_ACTION_TYPES, MarketplaceActionTypes } from 'actionTypes/algo';
import { API_STATUS } from 'constants/algo';
import { parseErrorMsg } from 'helpers/utils';
import { AlgoMarketplace, Subscriptions, StrategyAnalytics } from 'types/algo';

const initialSubscriptionsState: Subscriptions = {
  active_user_strategies: [],
  current: '',
  deployed: '',
  exited_user_strategies: [],
  harvest_profits: '',
  premiums_received: '',
  realized_pnl: '',
  roi: '',
  total_pnl: '',
  unrealized_cashflows: '',
};

const initialStrategyAnalyticsState: StrategyAnalytics = {
  avg_pnl_per_trade: '',
  chart: [],
  pnl: '',
  win_rate: '',
  avg_profit_per_trade: '',
  avg_loss_per_trade: '',
  no_of_trades: '',
};

const INITIAL_STATE: AlgoMarketplace = {
  // ------ Client State ------
  selectedStrategy: null,
  copyTradeModal: false,
  addFundsModal: false,
  exitStrategyModal: false,
  forceExitConfirmModal: false,
  trailingStopLossModal: false,

  // ------ Server State ------

  // strategy specific data (public)
  strategies: {
    data: [],
    status: API_STATUS.IDLE,
  },
  liveMetricsByTimeRange: {
    data: {},
    status: API_STATUS.IDLE,
  },
  // User specific data (private)
  subscriptions: {
    data: initialSubscriptionsState,
    status: API_STATUS.IDLE,
  },
  // trades which strategy have traded (public)
  strategyTrades: {
    data: [],
    status: API_STATUS.IDLE,
  },
  // trades which strategy have traded comes in user portion (private)
  strategyUserTrades: {
    data: [],
    status: API_STATUS.IDLE,
  },
  // Current open positions by strategy (public)
  strategyPositions: {
    data: [],
    status: API_STATUS.IDLE,
  },
  // Uses for dashboard page
  strategyAnalytics: {
    data: initialStrategyAnalyticsState,
    status: API_STATUS.IDLE,
  },
  addFunds: {
    status: API_STATUS.IDLE,
    error: null,
  },
  exitStrategy: {
    status: API_STATUS.IDLE,
    error: null,
  },
  forceExitStrategy: {
    status: API_STATUS.IDLE,
    error: null,
  },
  updateRiskValue: {
    status: API_STATUS.IDLE,
    error: null,
  },
  cancelExit: {
    status: API_STATUS.IDLE,
    error: null,
  },
};

const algo = produce((draft, action) => {
  switch (action.type) {
    case MarketplaceActionTypes.OPEN_COPY_TRADE_MODAL: {
      draft.selectedStrategy = action.payload.strategy;
      draft.copyTradeModal = true;
      break;
    }

    case MarketplaceActionTypes.CLOSE_COPY_TRADE_MODAL: {
      draft.selectedStrategy = null;
      draft.copyTradeModal = false;
      draft.addFunds.status = API_STATUS.IDLE;
      draft.addFunds.error = null;

      break;
    }

    case MarketplaceActionTypes.OPEN_ADD_FUNDS_MODAL: {
      draft.selectedStrategy = action.payload.strategy;
      draft.addFundsModal = true;
      break;
    }

    case MarketplaceActionTypes.CLOSE_ADD_FUNDS_MODAL: {
      draft.selectedStrategy = null;
      draft.addFundsModal = false;
      draft.addFunds.status = API_STATUS.IDLE;
      draft.addFunds.error = null;
      break;
    }

    case MarketplaceActionTypes.OPEN_EXIT_STRATEGY_MODAL: {
      draft.selectedStrategy = action.payload.strategy;
      draft.exitStrategyModal = true;
      break;
    }

    case MarketplaceActionTypes.CLOSE_EXIT_STRATEGY_MODAL: {
      draft.selectedStrategy = null;
      draft.exitStrategyModal = false;
      draft.exitStrategy.status = API_STATUS.IDLE;
      draft.forceExitStrategy.status = API_STATUS.IDLE;
      break;
    }

    case MarketplaceActionTypes.OPEN_FORCE_EXIT_CONFIRM_MODAL: {
      draft.selectedStrategy = action.payload.strategy;
      draft.forceExitConfirmModal = true;
      break;
    }

    case MarketplaceActionTypes.CLOSE_FORCE_EXIT_CONFIRM_MODAL: {
      draft.selectedStrategy = null;
      draft.forceExitConfirmModal = false;
      draft.forceExitStrategy.status = API_STATUS.IDLE;
      break;
    }

    case MarketplaceActionTypes.OPEN_TRAILING_STOP_LOSS_MODAL: {
      draft.selectedStrategy = action.payload.strategy;
      draft.trailingStopLossModal = true;
      break;
    }

    case MarketplaceActionTypes.CLOSE_TRAILING_STOP_LOSS_MODAL: {
      draft.selectedStrategy = null;
      draft.trailingStopLossModal = false;
      draft.updateRiskValue.error = null;
      draft.updateRiskValue.status = API_STATUS.IDLE;
      break;
    }

    case MarketplaceActionTypes.RESET_ADD_FUNDS_STATUS: {
      draft.addFunds.status = API_STATUS.IDLE;
      break;
    }

    case MarketplaceActionTypes.RESET_UPDATE_RISK_VALUE_STATUS: {
      draft.updateRiskValue.status = API_STATUS.IDLE;
      break;
    }

    case MarketplaceActionTypes.RESET_SUBSCRIPTIONS: {
      draft.subscriptions.data = initialSubscriptionsState;
      draft.subscriptions.status = API_STATUS.IDLE;
      break;
    }

    case MarketplaceActionTypes.RESET_STRATEGY_ANALYTICS: {
      draft.strategyAnalytics.data = initialStrategyAnalyticsState;
      draft.strategyAnalytics.status = API_STATUS.IDLE;
      break;
    }

    case MarketplaceActionTypes.UPDATE_SELECTED_STRATEGY: {
      if (draft.selectedStrategy) {
        draft.selectedStrategy.stats.aum = String(
          Number(draft.selectedStrategy.stats.aum) + action.payload.amount
        );
        draft.selectedStrategy.stats.subscribers = action.payload.subscribers;
      }
      break;
    }

    case MarketplaceActionTypes.RESET_STRATEGY_TRADES: {
      draft.strategyTrades.data = [];
      draft.strategyTrades.status = API_STATUS.IDLE;
      break;
    }

    // API actions
    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGIES].LOAD: {
      draft.strategies.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGIES].FAIL: {
      draft.strategies.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGIES].SUCCESS: {
      draft.strategies.status = API_STATUS.SUCCESS;
      draft.strategies.data = action.result.result;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_TIME_RANGE_METRICS_DATA].LOAD: {
      draft.liveMetricsByTimeRange.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_TIME_RANGE_METRICS_DATA].FAIL: {
      draft.liveMetricsByTimeRange.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_TIME_RANGE_METRICS_DATA].SUCCESS: {
      const activeTimestamp = action.timeRange;
      draft.liveMetricsByTimeRange.status = API_STATUS.SUCCESS;
      draft.liveMetricsByTimeRange.data[activeTimestamp] = action.result.result;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_SUBSCRIPTIONS].LOAD: {
      draft.subscriptions.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_SUBSCRIPTIONS].FAIL: {
      draft.subscriptions.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_SUBSCRIPTIONS].SUCCESS: {
      draft.subscriptions.status = API_STATUS.SUCCESS;
      draft.subscriptions.data = action.result.result;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_TRADES].LOAD: {
      draft.strategyTrades.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_TRADES].FAIL: {
      draft.strategyTrades.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_TRADES].SUCCESS: {
      draft.strategyTrades.status = API_STATUS.SUCCESS;
      draft.strategyTrades.data = action.result.result;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_USER_TRADES].LOAD: {
      draft.strategyUserTrades.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_USER_TRADES].FAIL: {
      draft.strategyUserTrades.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_USER_TRADES].SUCCESS: {
      draft.strategyUserTrades.status = API_STATUS.SUCCESS;
      draft.strategyUserTrades.data = action.result.result;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_POSITIONS].LOAD: {
      draft.strategyPositions.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_POSITIONS].FAIL: {
      draft.strategyPositions.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_POSITIONS].SUCCESS: {
      draft.strategyPositions.status = API_STATUS.SUCCESS;
      draft.strategyPositions.data = action.result.result;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_ANALYTICS].LOAD: {
      draft.strategyAnalytics.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_ANALYTICS].FAIL: {
      draft.strategyAnalytics.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.GET_STRATEGY_ANALYTICS].SUCCESS: {
      draft.strategyAnalytics.status = API_STATUS.SUCCESS;
      draft.strategyAnalytics.data = action.result.result;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.ADD_FUNDS].LOAD: {
      draft.addFunds.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.ADD_FUNDS].FAIL: {
      const { error } = parseErrorMsg(action?.error);

      draft.addFunds.status = API_STATUS.FAILED;
      draft.addFunds.error = error;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.ADD_FUNDS].SUCCESS: {
      draft.addFunds.status = API_STATUS.SUCCESS;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.UPDATE_RISK_VALUE].LOAD: {
      draft.updateRiskValue.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.UPDATE_RISK_VALUE].FAIL: {
      const { error } = parseErrorMsg(action?.error);
      draft.updateRiskValue.status = API_STATUS.FAILED;
      draft.updateRiskValue.error = error;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.UPDATE_RISK_VALUE].SUCCESS: {
      draft.updateRiskValue.status = API_STATUS.SUCCESS;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.EXIT].LOAD: {
      draft.exitStrategy.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.EXIT].FAIL: {
      draft.exitStrategy.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.EXIT].SUCCESS: {
      draft.exitStrategy.status = API_STATUS.SUCCESS;

      const updatedStrategy = action.result.result;
      const strategies = draft.subscriptions.data.active_user_strategies;

      const index = strategies.findIndex(
        s => s.strategy_id === updatedStrategy.strategy_id
      );

      if (index !== -1) {
        strategies[index] = {
          ...strategies[index],
          initial_balance: updatedStrategy.initial_balance,
          current_balance: updatedStrategy.current_balance,
          state: updatedStrategy.state,
        };
      }
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.FORCE_EXIT].LOAD: {
      draft.forceExitStrategy.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.FORCE_EXIT].FAIL: {
      draft.forceExitStrategy.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.FORCE_EXIT].SUCCESS: {
      draft.forceExitStrategy.status = API_STATUS.SUCCESS;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.CANCEL_EXIT].LOAD: {
      draft.cancelExit.status = API_STATUS.LOADING;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.CANCEL_EXIT].FAIL: {
      draft.cancelExit.status = API_STATUS.FAILED;
      break;
    }

    case ALGO_ACTION_TYPES[MarketplaceActionTypes.CANCEL_EXIT].SUCCESS: {
      draft.cancelExit.status = API_STATUS.SUCCESS;
      break;
    }

    default:
      break;
  }
}, INITIAL_STATE);

export default algo;
